import jsPDF from 'jspdf';

// Utility function to format date as DD-MM-YYYY
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Utility function to format currency
const formatCurrency = (amount) => {
  return `₹${amount.toLocaleString('en-IN')}`;
};

// Generate PDF for company report
export const generateCompanyReportPDF = (companyData, summaryData) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  let yPosition = 20;

  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Company Report', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 15;

  // Summary section
  if (summaryData) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary', 20, yPosition);
    yPosition += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Bills: ${formatCurrency(summaryData.totalBills)}`, 20, yPosition);
    yPosition += 6;
    doc.text(`Total Payments: ${formatCurrency(summaryData.totalPayments)}`, 20, yPosition);
    yPosition += 6;
    doc.text(`Pending Amount: ${formatCurrency(summaryData.pendingAmount)}`, 20, yPosition);
    yPosition += 15;
  }

  // Company breakdown
  if (companyData && companyData.companyBreakdown) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Company Breakdown', 20, yPosition);
    yPosition += 10;

    companyData.companyBreakdown.forEach((company, companyIndex) => {
      // Check if we need a new page
      if (yPosition > pageHeight - 100) {
        doc.addPage();
        yPosition = 20;
      }

      // Company header
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`${companyIndex + 1}. ${company.companyName}`, 20, yPosition);
      yPosition += 8;

      // Company totals
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total Bills: ${formatCurrency(company.totalBills)}`, 25, yPosition);
      yPosition += 6;
      doc.text(`Total Payments: ${formatCurrency(company.totalPayments)}`, 25, yPosition);
      yPosition += 6;
      doc.text(`Pending Amount: ${formatCurrency(company.pendingAmount)}`, 25, yPosition);
      yPosition += 10;

      // Bills table
      if (company.bills && company.bills.length > 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Bills:', 25, yPosition);
        yPosition += 8;

        // Table headers
        const tableHeaders = ['Date', 'Party', 'Bill #', 'Amount', 'Paid', 'Pending', 'Status'];
        const colWidths = [25, 30, 25, 25, 20, 20, 15];
        let xPosition = 25;

        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        tableHeaders.forEach((header, index) => {
          doc.text(header, xPosition, yPosition);
          xPosition += colWidths[index];
        });
        yPosition += 6;

        // Table rows
        doc.setFont('helvetica', 'normal');
        company.bills.forEach((bill) => {
          if (yPosition > pageHeight - 20) {
            doc.addPage();
            yPosition = 20;
          }

          xPosition = 25;
          const rowData = [
            formatDate(bill.billDate),
            bill.party?.name || 'N/A',
            bill.billNumber,
            formatCurrency(bill.amount),
            formatCurrency(bill.paidAmount),
            formatCurrency(bill.pendingAmount),
            bill.status
          ];

          rowData.forEach((data, index) => {
            doc.text(data.toString(), xPosition, yPosition);
            xPosition += colWidths[index];
          });
          yPosition += 5;
        });
        yPosition += 10;
      }

      // Payments table
      if (company.payments && company.payments.length > 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Payments:', 25, yPosition);
        yPosition += 8;

        // Table headers
        const tableHeaders = ['Date', 'Party', 'Payment #', 'Amount', 'Status'];
        const colWidths = [25, 30, 25, 25, 20];
        let xPosition = 25;

        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        tableHeaders.forEach((header, index) => {
          doc.text(header, xPosition, yPosition);
          xPosition += colWidths[index];
        });
        yPosition += 6;

        // Table rows
        doc.setFont('helvetica', 'normal');
        company.payments.forEach((payment) => {
          if (yPosition > pageHeight - 20) {
            doc.addPage();
            yPosition = 20;
          }

          xPosition = 25;
          const rowData = [
            formatDate(payment.paymentDate),
            payment.party?.name || 'N/A',
            payment.paymentNumber || 'N/A',
            formatCurrency(payment.amount),
            payment.status
          ];

          rowData.forEach((data, index) => {
            doc.text(data.toString(), xPosition, yPosition);
            xPosition += colWidths[index];
          });
          yPosition += 5;
        });
        yPosition += 15;
      }
    });
  }

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, pageHeight - 10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-GB')}`, 20, pageHeight - 10);
  }

  return doc;
};

// Generate PDF for party report
export const generatePartyReportPDF = (partyData, summaryData) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  let yPosition = 20;

  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Party Report', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 15;

  // Summary section
  if (summaryData) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary', 20, yPosition);
    yPosition += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Bills: ${formatCurrency(summaryData.totalBills)}`, 20, yPosition);
    yPosition += 6;
    doc.text(`Total Payments: ${formatCurrency(summaryData.totalPayments)}`, 20, yPosition);
    yPosition += 6;
    doc.text(`Pending Amount: ${formatCurrency(summaryData.pendingAmount)}`, 20, yPosition);
    yPosition += 15;
  }

  // Party breakdown
  if (partyData && partyData.partyBreakdown) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Party Breakdown', 20, yPosition);
    yPosition += 10;

    partyData.partyBreakdown.forEach((party, partyIndex) => {
      // Check if we need a new page
      if (yPosition > pageHeight - 100) {
        doc.addPage();
        yPosition = 20;
      }

      // Party header
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`${partyIndex + 1}. ${party.partyName}`, 20, yPosition);
      yPosition += 8;

      // Party totals
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total Bills: ${formatCurrency(party.totalBills)}`, 25, yPosition);
      yPosition += 6;
      doc.text(`Total Payments: ${formatCurrency(party.totalPayments)}`, 25, yPosition);
      yPosition += 6;
      doc.text(`Pending Amount: ${formatCurrency(party.pendingAmount)}`, 25, yPosition);
      yPosition += 10;

      // Bills table
      if (party.bills && party.bills.length > 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Bills:', 25, yPosition);
        yPosition += 8;

        // Table headers
        const tableHeaders = ['Date', 'Company', 'Bill #', 'Amount', 'Paid', 'Pending', 'Status'];
        const colWidths = [25, 30, 25, 25, 20, 20, 15];
        let xPosition = 25;

        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        tableHeaders.forEach((header, index) => {
          doc.text(header, xPosition, yPosition);
          xPosition += colWidths[index];
        });
        yPosition += 6;

        // Table rows
        doc.setFont('helvetica', 'normal');
        party.bills.forEach((bill) => {
          if (yPosition > pageHeight - 20) {
            doc.addPage();
            yPosition = 20;
          }

          xPosition = 25;
          const rowData = [
            formatDate(bill.billDate),
            bill.company?.name || 'N/A',
            bill.billNumber,
            formatCurrency(bill.amount),
            formatCurrency(bill.paidAmount),
            formatCurrency(bill.pendingAmount),
            bill.status
          ];

          rowData.forEach((data, index) => {
            doc.text(data.toString(), xPosition, yPosition);
            xPosition += colWidths[index];
          });
          yPosition += 5;
        });
        yPosition += 10;
      }

      // Payments table
      if (party.payments && party.payments.length > 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Payments:', 25, yPosition);
        yPosition += 8;

        // Table headers
        const tableHeaders = ['Date', 'Company', 'Payment #', 'Amount', 'Status'];
        const colWidths = [25, 30, 25, 25, 20];
        let xPosition = 25;

        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        tableHeaders.forEach((header, index) => {
          doc.text(header, xPosition, yPosition);
          xPosition += colWidths[index];
        });
        yPosition += 6;

        // Table rows
        doc.setFont('helvetica', 'normal');
        party.payments.forEach((payment) => {
          if (yPosition > pageHeight - 20) {
            doc.addPage();
            yPosition = 20;
          }

          xPosition = 25;
          const rowData = [
            formatDate(payment.paymentDate),
            payment.company?.name || 'N/A',
            payment.paymentNumber || 'N/A',
            formatCurrency(payment.amount),
            payment.status
          ];

          rowData.forEach((data, index) => {
            doc.text(data.toString(), xPosition, yPosition);
            xPosition += colWidths[index];
          });
          yPosition += 5;
        });
        yPosition += 15;
      }
    });
  }

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, pageHeight - 10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-GB')}`, 20, pageHeight - 10);
  }

  return doc;
};

// Generate PDF for filtered report (when company or party is selected)
export const generateFilteredReportPDF = (companyData, partyData, summaryData, filters) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  let yPosition = 20;

  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Filtered Report', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 15;

  // Filter info
  if (filters.company || filters.party) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Filter Applied:', 20, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    if (filters.company) {
      doc.text(`Company: ${filters.company.name}`, 25, yPosition);
      yPosition += 6;
    }
    if (filters.party) {
      doc.text(`Party: ${filters.party.name}`, 25, yPosition);
      yPosition += 6;
    }
    yPosition += 10;
  }

  // Summary section
  if (summaryData) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary', 20, yPosition);
    yPosition += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Bills: ${formatCurrency(summaryData.totalBills)}`, 20, yPosition);
    yPosition += 6;
    doc.text(`Total Payments: ${formatCurrency(summaryData.totalPayments)}`, 20, yPosition);
    yPosition += 6;
    doc.text(`Pending Amount: ${formatCurrency(summaryData.pendingAmount)}`, 20, yPosition);
    yPosition += 15;
  }

  // Generate company report if company data exists
  if (companyData && companyData.companyBreakdown) {
    const companyDoc = generateCompanyReportPDF(companyData, null);
    const companyPages = companyDoc.internal.getNumberOfPages();
    
    for (let i = 1; i <= companyPages; i++) {
      if (i > 1) {
        doc.addPage();
      }
      // Copy page content (simplified - in real implementation, you'd need to copy the actual content)
    }
  }

  // Generate party report if party data exists
  if (partyData && partyData.partyBreakdown) {
    const partyDoc = generatePartyReportPDF(partyData, null);
    const partyPages = partyDoc.internal.getNumberOfPages();
    
    for (let i = 1; i <= partyPages; i++) {
      if (i > 1) {
        doc.addPage();
      }
      // Copy page content (simplified - in real implementation, you'd need to copy the actual content)
    }
  }

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, pageHeight - 10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-GB')}`, 20, pageHeight - 10);
  }

  return doc;
};
